<script lang="ts" setup>
import type { RouteLocationNormalizedLoaded, RouteLocationRaw, Router } from "vue-router"
import { computed, inject } from "vue"
import { routeLocationKey, routerKey, RouterLink } from "vue-router"
import { resolveSlugPersistedPath } from "@/slugRoutes"

interface AppLinkProps {
  to: string | RouteLocationRaw
}

const props = defineProps<AppLinkProps>()

// Use inject() with an explicit null default instead of calling useRoute()/
// useRouter() directly. useRoute/useRouter call inject() without a default,
// which causes Vue to emit a "[Vue warn]: injection not found" console warning
// whenever the router plugin is not provided — which happens in many component
// tests that don't care about routing. inject(..., null) returns null silently
// in that case, and the !route / !router guard below falls through to the
// original `to` unchanged, which is the correct passthrough behaviour.
const route = inject<RouteLocationNormalizedLoaded | null>(routeLocationKey, null)
const router = inject<Router | null>(routerKey, null)

const resolvedTo = computed<string | RouteLocationRaw>(() => {
  try {
    if (!route || !router)
      return props.to

    return resolveSlugPersistedPath(route, router.resolve(props.to)) ?? props.to
  }
  catch {
    return props.to
  }
})
</script>

<template>
  <RouterLink v-bind="$attrs" :to="resolvedTo">
    <slot />
  </RouterLink>
</template>
